import { useState } from "react";

import { t } from "i18next";
import { isNotEmpty } from "neetocist";
import { taxonomies, globalProps } from "neetocommons/initializers";
import { Alert, NoData, Table, Toastr } from "neetoui";
import PropTypes from "prop-types";
import { prop, pick, keys } from "ramda";
import { Trans } from "react-i18next";

import Container from "components/Container";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import TableWrapper from "components/TableWrapper";

import EditPane from "./EditPane";
import LeftActionBlock from "./LeftActionBlock";
import { useBulkDestroyTaxonomies } from "./useTaxonomyApi";
import {
  formatDataForTable,
  getColumnData,
  getFilteredTaxonomies,
} from "./utils";

const Taxonomy = ({
  breadcrumbs,
  columnLabels = {},
  columnsToShow = {},
  titleHelpPopoverProps = {},
}) => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isEditTaxonomyPaneOpen, setIsEditTaxonomyPaneOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [currentKeys, setCurrentKeys] = useState([]);

  const { mutate: bulkDestroyTaxonomies } = useBulkDestroyTaxonomies();

  const hostTaxonomyKeys = keys(globalProps.taxonomies);
  const hostTaxonomies = pick(hostTaxonomyKeys, taxonomies);

  const [filteredTaxonomies, setFilteredTaxonomies] = useState(hostTaxonomies);

  const handleRowSelect = ids => setSelectedRowIds(ids);

  const handleDelete = () => {
    const payload = { keyNames: currentKeys };

    const options = {
      onSuccess: () => window.location.reload(),
      onError: error => Toastr.error(error),
    };

    bulkDestroyTaxonomies(payload, options);
  };

  const handleEdit = values => {
    setInitialValues(values);
    setIsEditTaxonomyPaneOpen(true);
  };

  const handleSearch = event => {
    setSearchString(event.target.value);
    const newTaxonomies = getFilteredTaxonomies(
      event.target.value,
      hostTaxonomies
    );
    setFilteredTaxonomies(newTaxonomies);
  };

  return (
    <Container>
      <Header
        {...{ breadcrumbs }}
        title={t("neetoMolecules.taxonomy.title")}
        searchProps={{
          onChange: handleSearch,
          value: searchString,
          placeholder: t("neetoMolecules.taxonomy.searchTaxonomy"),
        }}
        titleHelpPopoverProps={{
          description: t("neetoMolecules.taxonomy.description"),
          title: t("neetoMolecules.taxonomy.title"),
          ...titleHelpPopoverProps,
        }}
      />
      <SubHeader
        leftActionBlock={
          <LeftActionBlock
            {...{ selectedRowIds, setCurrentKeys, setIsResetModalOpen }}
            selectedCount={selectedRowIds.length}
            totalCount={Object.entries(filteredTaxonomies).length}
          />
        }
      />
      {isNotEmpty(filteredTaxonomies) ? (
        <TableWrapper>
          <Table
            fixedHeight
            rowSelection
            allowRowClick={false}
            rowData={formatDataForTable(filteredTaxonomies)}
            rowKey={prop("keyName")}
            selectedRowKeys={selectedRowIds}
            tableLayout="fixed"
            columnData={getColumnData({
              columnLabels,
              columnsToShow,
              handleEdit,
              setCurrentKeys,
              setIsResetModalOpen,
            })}
            onRowSelect={handleRowSelect}
          />
          <EditPane
            {...{ columnLabels, columnsToShow, initialValues }}
            isOpen={isEditTaxonomyPaneOpen}
            onClose={() => setIsEditTaxonomyPaneOpen(false)}
          />
          <Alert
            isOpen={isResetModalOpen}
            submitButtonLabel={t("neetoMolecules.common.actions.proceed")}
            title={t("neetoMolecules.taxonomy.resetAlert.title")}
            message={
              <Trans
                i18nKey="neetoMolecules.taxonomy.resetAlert.message"
                values={{
                  count: currentKeys.length,
                  what: t(`taxonomyDefaultLabels.${currentKeys[0]}.singular`),
                }}
              />
            }
            onClose={() => setIsResetModalOpen(false)}
            onSubmit={handleDelete}
          />
        </TableWrapper>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <NoData title={t("neetoMolecules.taxonomy.noData")} />
        </div>
      )}
    </Container>
  );
};

Taxonomy.propTypes = {
  /**
   * To show breadcrumbs. `text` accepts a text string to be shown as the breadcrumb and `link` accepts relative URL path for the breadcrumb.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  /**
   * To provide custom column labels for the table.
   */
  columnLabels: PropTypes.object,
  /**
   * To show/hide columns in the table.
   */
  columnsToShow: PropTypes.object,
  /**
   * Props to be passed to the help popover in the title.
   */
  titleHelpPopoverProps: PropTypes.object,
};

export default Taxonomy;
